import type { PaginationQuery } from '../../../types/pagination';
import { appendPaginationQuery } from '../../../types/pagination';
import type { ApiClient } from '../client';
import type { Uuid } from '../types/common';
import type {
  BoardReadinessResponse,
  CampusCompareResponse,
  CreateParentConsentRequest,
  DataExitExportResponse,
  ActivePickupPinResponse,
  ListPickupPinAuditsResponse,
  ListBusPickupWindowsResponse,
  ListParentConsentsResponse,
  ListSubstitutionSuggestionsResponse,
  ListWhatsAppActionTemplatesResponse,
  PickupPinIssueRequest,
  PickupPinIssueResponse,
  PickupPinVerifyRequest,
  PickupPinVerifyResponse,
  PublicPickupPinLookupResponse,
  RiskInsightsResponse,
  SendWhatsAppActionRequest,
  TransferPackRequest,
  TransferPackResponse,
} from '../types/exclusive-ops';

export function bindExclusiveOps(client: ApiClient) {
  return {
    async issuePickupPin(body: PickupPinIssueRequest) {
      return client.request<PickupPinIssueResponse>('/security/pickup-pins/issue', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async verifyPickupPin(body: PickupPinVerifyRequest) {
      return client.request<PickupPinVerifyResponse>('/security/pickup-pins/verify', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /** Who issued / verified pickup PINs (audit trail). */
    async listPickupPinAudits(params?: { student_id?: string; limit?: number }) {
      const qs = new URLSearchParams();
      if (params?.student_id) qs.set('student_id', params.student_id);
      if (params?.limit != null) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return client.request<ListPickupPinAuditsResponse>(
        query ? `/security/pickup-pins/audits?${query}` : '/security/pickup-pins/audits'
      );
    },

    /** Active PIN for a student (staff). */
    async getActivePickupPin(studentId: string, params?: { date?: string }) {
      const qs = new URLSearchParams();
      qs.set('student_id', studentId);
      if (params?.date) qs.set('date', params.date);
      return client.request<ActivePickupPinResponse>(
        `/security/pickup-pins/active?${qs.toString()}`
      );
    },

    /**
     * Soft public lookup for QR landing page — must not consume the PIN.
     * Falls back to verify only when the dedicated lookup route is missing.
     */
    async lookupPickupPinPublic(pin: string) {
      const code = encodeURIComponent(pin.trim());
      try {
        return await client.request<PublicPickupPinLookupResponse>(
          `/public/verify/pickup/${code}`
        );
      } catch {
        return client.request<PublicPickupPinLookupResponse>(
          `/security/pickup-pins/lookup?pin=${code}`
        );
      }
    },

    async listBusPickupWindows(params?: { date?: string }) {
      const qs = new URLSearchParams();
      if (params?.date) qs.set('date', params.date);
      const query = qs.toString();
      const path = query ? `/security/bus-pickup-windows?${query}` : '/security/bus-pickup-windows';
      return client.request<ListBusPickupWindowsResponse>(path);
    },

    async listWhatsAppActionTemplates() {
      return client.request<ListWhatsAppActionTemplatesResponse>(
        '/communication/whatsapp/action-templates'
      );
    },

    async sendWhatsAppAction(body: SendWhatsAppActionRequest) {
      return client.request<{ job_id?: string; sent?: number }>('/communication/whatsapp/actions/send', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listParentConsents(params?: PaginationQuery & { status?: string }) {
      const qs = new URLSearchParams();
      appendPaginationQuery(qs, params);
      if (params?.status?.trim()) qs.set('status', params.status.trim());
      const query = qs.toString();
      const path = query ? `/portal/consents?${query}` : '/portal/consents';
      return client.request<ListParentConsentsResponse>(path);
    },

    async createParentConsent(body: CreateParentConsentRequest) {
      return client.request<{ consent_id: Uuid }>('/portal/consents', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async listSubstitutionSuggestions(params?: { date?: string; absent_staff_id?: string }) {
      const qs = new URLSearchParams();
      if (params?.date) qs.set('date', params.date);
      if (params?.absent_staff_id) qs.set('absent_staff_id', params.absent_staff_id);
      const query = qs.toString();
      const path = query
        ? `/teacher-portal/substitutions?${query}`
        : '/teacher-portal/substitutions';
      return client.request<ListSubstitutionSuggestionsResponse>(path);
    },

    async markStaffAbsence(body: { date: string; staff_id: string; reason?: string }) {
      return client.request<{ absence_id: Uuid }>('/teacher-portal/substitutions/absences', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async assignSubstitution(body: {
      date: string;
      period_label: string;
      class_name: string;
      teacher_id: string;
    }) {
      return client.request<{ assignment_id: Uuid }>('/teacher-portal/substitutions/assign', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async getBoardReadiness(params?: { class_id?: string; exam_label?: string }) {
      const qs = new URLSearchParams();
      if (params?.class_id) qs.set('class_id', params.class_id);
      if (params?.exam_label) qs.set('exam_label', params.exam_label);
      const query = qs.toString();
      const path = query ? `/exams/board-readiness?${query}` : '/exams/board-readiness';
      return client.request<BoardReadinessResponse>(path);
    },

    async createTransferPack(body: TransferPackRequest) {
      return client.request<TransferPackResponse>('/certificates/transfer-packs', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    async getCampusCompare() {
      return client.request<CampusCompareResponse>('/school/campus-compare');
    },

    async getRiskInsights() {
      return client.request<RiskInsightsResponse>('/ai/risk-insights');
    },

    async requestDataExitExport(body?: { include_media?: boolean }) {
      return client.request<DataExitExportResponse>('/school/data-exit-export', {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      });
    },
  };
}
